import { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Seo from "@/components/Seo";
import api from "@/lib/api";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";

export default function PaymentSuccess() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking, paid, expired, error
  const [attempts, setAttempts] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;
    const poll = async (n) => {
      if (cancelled || n > 15) { if (!cancelled && n > 15) setStatus("error"); return; }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          setTimeout(() => navigate(`/project/${id}`), 1500);
          return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
        setAttempts(n);
        timerRef.current = setTimeout(() => poll(n + 1), 2000);
      } catch {
        setStatus("error");
      }
    };
    poll(1);
    return () => { cancelled = true; if (timerRef.current) clearTimeout(timerRef.current); };
  }, [sessionId, id, navigate]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--bw-bg-primary)" }}>
      <Seo
        title="Confirming Your Payment — BuildWise AI"
        description="Verifying your BuildWise AI purchase. Your premium report or concierge submission is being prepared."
        path={`/project/${id}/payment-success`}
      />
      <Header />
      <div className="max-w-md mx-auto py-24 px-6 text-center" data-testid="payment-status-page">
        {status === "checking" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto mb-4 animate-spin" style={{ color: "var(--bw-brand)" }} />
            <div className="label-overline mb-2">Verifying Payment</div>
            <h1 className="font-display font-black text-3xl tracking-tight mb-3">Confirming your purchase…</h1>
            <p className="text-sm" style={{ color: "var(--bw-text-secondary)" }}>Attempt {attempts} of 15</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 mx-auto mb-4" style={{ color: "var(--bw-success)" }} />
            <div className="label-overline mb-2" style={{ color: "var(--bw-success)" }}>Payment Confirmed</div>
            <h1 className="font-display font-black text-3xl tracking-tight mb-3">You're unlocked!</h1>
            <p className="text-sm" style={{ color: "var(--bw-text-secondary)" }}>Generating your premium report…</p>
          </>
        )}
        {(status === "expired" || status === "error") && (
          <>
            <AlertCircle className="w-10 h-10 mx-auto mb-4" style={{ color: "var(--bw-error)" }} />
            <h1 className="font-display font-black text-3xl tracking-tight mb-3">Payment issue</h1>
            <p className="text-sm mb-6" style={{ color: "var(--bw-text-secondary)" }}>
              We couldn't confirm your payment. Please return to your project and try again.
            </p>
            <button onClick={() => navigate(`/project/${id}`)} className="btn-primary" data-testid="back-project-btn">
              Back to Project
            </button>
          </>
        )}
      </div>
    </div>
  );
}
